import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline } from "@/components/primitives";
import { StewardStoneNav } from "@/components/StewardStoneNav";

const SECTIONS = [
  { t: "Hospital & Health System Operations", d: "Consulting grounded in the relationship between care delivery, staffing, and administration within daily hospital and health-system operations." },
  { t: "Organizational & Operational Assessment", d: "Examining observed practice, institutional expectations, and the conditions that explain the differences between them." },
  { t: "Workflow & Gap Analysis", d: "Identifying process failures, accumulated risk, and the operational conditions that generate delay, rework, and lost value." },
  { t: "Performance Improvement Planning", d: "Practical strategies to strengthen operations, reduce friction, and improve measurable outcomes." },
  { t: "Leadership & Executive Advisory", d: "Working with leadership to translate complex operational findings into defined priorities, responsibilities, and implementation decisions." },
  { t: "Implementation Strategy", d: "Actionable improvement planning an organization can realistically execute and sustain." },
];

export default function HealthcareAdvisory() {
  return (
    <div className="glow-navy jmh-page">
      <Seo title="Healthcare Advisory — Steward & Stone" description="Steward & Stone Healthcare Advisory — hospital and healthcare operational consulting: organizational and operational assessment, workflow analysis, performance improvement, and implementation strategy." />
      <StewardStoneNav />

      <section className="text-center jmh-section jmh-page-opening">
        <Reveal>
          <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Steward &amp; Stone</p>
          <h1 className="mt-3 font-serif2 text-4xl md:text-5xl text-[var(--bone)] leading-tight">Healthcare Advisory</h1>
          <div className="mt-6 mx-auto space-y-4 text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed text-left jmh-content-shell">
            <p>Healthcare professionals should be supported by operational systems that make responsible care possible without relying indefinitely on individual endurance to compensate for institutional shortcomings.</p>
            <p>When staffing, communication, workflow, leadership decisions, and financial pressures fall out of alignment, the burden does not remain inside an administrative report. It reaches the nurse trying to manage an impossible assignment, the physician waiting for information that should already be available, and the patient experiencing the delay.</p>
            <p>Steward &amp; Stone examines the operational conditions behind those experiences. We compare intended processes with observed practice, identify the factors sustaining the gap, and determine which changes can be implemented responsibly.</p>
          </div>
        </Reveal>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><Hairline className="opacity-70 mb-12" /></Reveal>
          <div className="jmh-content-grid">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="corner-frame px-7 py-8 h-full">
                  <h3 className="font-serif2 text-xl md:text-2xl text-[var(--bone)]">{s.t}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="text-center jmh-section">
        <Reveal className="mx-auto jmh-content-shell">
          <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)] leading-loose">
            Steward &amp; Stone provides strategic and operational advisory only. We do not provide clinical, medical, legal, accounting, or licensed investment services.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8"><PillButton to="/steward-and-stone/assessment" testid="healthcare-inquiry">Request a Review</PillButton></Reveal>
      </section>
    </div>
  );
}
